import { useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const SEO = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    useEffect(() => {
        // Update Title
        document.title = t('seo.title');

        // Update Meta Description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', t('seo.description'));

        // Update Meta Keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.name = 'keywords';
            document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', t('seo.keywords'));

        // Update Open Graph Tags
        const updateOGTag = (property, content) => {
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        updateOGTag('og:title', t('seo.title'));
        updateOGTag('og:description', t('seo.description'));
        updateOGTag('og:locale', language === 'ar' ? 'ar_SA' : 'en_US');
        updateOGTag('og:type', 'website');
        updateOGTag('og:site_name', t('nav.brand'));

        // Update Twitter Tags
        const updateTwitterTag = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        updateTwitterTag('twitter:card', 'summary_large_image');
        updateTwitterTag('twitter:title', t('seo.title'));
        updateTwitterTag('twitter:description', t('seo.description'));

        // Inject Robots Directives for Search Engines
        const updateRobotsTag = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.name = name;
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };
        updateRobotsTag('robots', 'index, follow');
        updateRobotsTag('googlebot', 'index, follow');
        updateRobotsTag('bingbot', 'index, follow');

        // Dynamic JSON-LD Structured Schema Data (Google Knowledge Graph & Local Pack SEO)
        let schemaScript = document.getElementById('jsonld-schema');
        if (!schemaScript) {
            schemaScript = document.createElement('script');
            schemaScript.id = 'jsonld-schema';
            schemaScript.type = 'application/ld+json';
            document.head.appendChild(schemaScript);
        }

        const schemaData = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": language === 'ar' ? "سهم الأنظمة القابضة" : "Saham Holding",
            "alternativeName": language === 'ar' ? "شركة سهم القابضة" : "Saham Systems Holding",
            "url": "https://saham-holding.com/",
            "logo": "https://saham-holding.com/src/data/d.png",
            "description": t('seo.description'),
            "address": {
                "@type": "PostalAddress",
                "addressLocality": language === 'ar' ? "الرياض" : "Riyadh",
                "addressRegion": language === 'ar' ? "منطقة الرياض" : "Riyadh Region",
                "addressCountry": language === 'ar' ? "المملكة العربية السعودية" : "Saudi Arabia"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+966110000000",
                "contactType": "customer support",
                "email": "info@saham-holding.com",
                "areaServed": "SA",
                "availableLanguage": ["Arabic", "English"]
            },
            "sameAs": [
                "https://twitter.com/sahamholding",
                "https://www.linkedin.com/company/saham-holding",
                "https://www.instagram.com/sahamholding"
            ],
            "knowsAbout": [
                "Digital Transformation",
                "Artificial Intelligence",
                "Virtual Reality",
                "Cloud Computing",
                "Cyber Security",
                "التحول الرقمي",
                "الذكاء الاصطناعي",
                "الواقع الافتراضي",
                "الأمن السيبراني"
            ]
        };
        schemaScript.textContent = JSON.stringify(schemaData, null, 2);

    }, [t, language]);

    return null;
};

export default SEO;
